/**
 * Pill shown on the phone when Android Auto is connected.
 *
 * Always renders as a compact top-center chip so the phone UI stays fully
 * usable. The driver should look at the car screen during navigation — the
 * phone is just a control surface. Pre-v1.0.199 this was a full take-over
 * banner that hid the map, which made the phone feel locked-out.
 *
 * Three visual states:
 *   - Idle:        🚗 Android Auto
 *   - Recording:   🚗 ● Android Auto   (red dot = trip recording)
 *   - Navigating:  🚗 Naviger til X · 12.3km  [Stopp]
 *
 * Tap the pill to expand into a detail bottom-sheet (future). For now,
 * tap-target is the Stop button when navigating.
 */
import React from 'react';

interface AndroidAutoBannerProps {
  isConnected: boolean;
  isNavigating: boolean;
  isRecording?: boolean;
  destinationName?: string;
  remainingMeters?: number;
  remainingSeconds?: number;
  onStopNavigation?: () => void;
  /** Tap the pill (anywhere except the Stop button) to open the route info sheet. */
  onClick?: () => void;
  /**
   * Whether the current user has Premium access. When false and the car is
   * connected, the pill switches to an upsell variant ("Premium kreves") that
   * opens the upgrade overlay instead of route info.
   */
  hasAccess?: boolean;
  /** Open the upgrade overlay (used by the upsell variant). */
  onUpgrade?: () => void;
}

export const AndroidAutoBanner: React.FC<AndroidAutoBannerProps> = ({
  isConnected,
  isNavigating,
  isRecording = false,
  destinationName,
  remainingMeters,
  remainingSeconds,
  onStopNavigation,
  onClick,
  hasAccess = true,
  onUpgrade,
}) => {
  if (!isConnected) return null;

  // Upsell variant — car plugged in but user isn't premium. Replaces the normal
  // pill with a tappable "Premium kreves" chip so the user understands why the
  // car screen is empty and has a single tap to upgrade.
  if (!hasAccess) {
    return (
      <div
        role="button"
        aria-live="polite"
        onClick={onUpgrade}
        style={{
          position: 'fixed',
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 76px)',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          background: 'linear-gradient(90deg, #4f46e5, #7c3aed)',
          color: '#fff',
          padding: '6px 12px',
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.2,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          boxShadow: '0 2px 10px rgba(79,70,229,0.4)',
          maxWidth: 'calc(100vw - 24px)',
          border: '1px solid rgba(255,255,255,0.25)',
          cursor: 'pointer',
        }}
      >
        <span aria-hidden style={{ fontSize: 14 }}>🚗</span>
        <span>Android Auto krever Premium</span>
        <span
          style={{
            background: 'rgba(255,255,255,0.18)',
            padding: '2px 8px',
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          Oppgrader
        </span>
      </div>
    );
  }

  const distText = remainingMeters != null
    ? remainingMeters < 1000
      ? `${Math.round(remainingMeters)} m`
      : `${(remainingMeters / 1000).toFixed(1)} km`
    : '';
  const etaText = remainingSeconds != null && remainingSeconds > 0
    ? remainingSeconds < 60
      ? `${Math.round(remainingSeconds)} s`
      : `${Math.round(remainingSeconds / 60)} min`
    : '';
  const clickable = isNavigating && !!onClick;

  return (
    <div
      role={clickable ? 'button' : 'status'}
      aria-live="polite"
      onClick={clickable ? onClick : undefined}
      style={{
        position: 'fixed',
        // Dock the pill at the bottom, above MobileBottomNav (~64px tall) so
        // it doesn't obscure the top search field. Bottom-anchored is also
        // closer to the steering-wheel/center-stack mental model.
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 76px)',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        background: 'rgba(8,16,32,0.92)',
        color: '#e0e7ff',
        padding: isNavigating ? '6px 6px 6px 12px' : '6px 12px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0.2,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
        maxWidth: 'calc(100vw - 24px)',
        border: '1px solid rgba(147,197,253,0.25)',
        cursor: clickable ? 'pointer' : 'default',
      }}
    >
      <span aria-hidden style={{ fontSize: 14 }}>🚗</span>

      {isRecording && (
        <span
          aria-label="Tur-opptak pågår"
          className="animate-pulse"
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#ef4444',
            display: 'inline-block',
            flexShrink: 0,
          }}
        />
      )}

      {isNavigating ? (
        <>
          <span style={{ color: '#fff' }}>
            Navigerer i bilen
          </span>
          {onStopNavigation && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onStopNavigation(); }}
              style={{
                background: '#dc2626',
                color: '#fff',
                border: 'none',
                padding: '4px 10px',
                borderRadius: 999,
                fontWeight: 700,
                fontSize: 11,
                cursor: 'pointer',
                marginLeft: 4,
              }}
              aria-label="Stopp navigasjon"
            >
              Stopp
            </button>
          )}
        </>
      ) : (
        <span>Android Auto</span>
      )}
    </div>
  );
};
